/**
 * How wide each fee-schedule column wants to be, and the order in which the table gives columns up.
 *
 * The table used to lay itself out `auto`, which for an HTML table means every column is sized to its
 * own longest cell. The cells ask to be truncated — `overflow: hidden; text-overflow: ellipsis;
 * white-space: nowrap` — but under auto layout that request can never be granted: `nowrap` makes a
 * cell's minimum content width equal its maximum, so the column simply grows to fit the longest
 * description and the ellipsis never appears. Five ordinary categories made the table 1433px wide, and
 * the columns past Description — Fixed?, Fee, Per, Team Member, Code — went off the right-hand edge
 * behind the pinned Actions column, with nothing on screen to say the table scrolled. A fee schedule
 * showing no fees (#4964). It did that at 1920px too; a narrow pane only made it worse.
 *
 * So the table is laid out `fixed` against these widths instead, and the ellipsis starts working. What
 * `fixed` cannot do is invent room, so below the width where every column still fits, columns come out
 * — least useful first. A fee schedule's job is to say what the work is and what it costs, so Name and
 * Fee are never candidates.
 */

/**
 * A column's preferred width, and the narrowest it can be and still be worth drawing.
 *
 * The minimums are the width of the *header*, not of the values underneath it: a column heading carries
 * a sort control and a column menu beside its text, so "Team Member" needs 170px to read as those two
 * words rather than as "Tea…", while the cells below it truncate happily at any width. A column too
 * narrow to name itself is worse than one that is not there, because the reader cannot tell what they
 * are looking at.
 */
export type FeeScheduleColumnWidth = {
  preferred: number;
  minimum: number;
  /**
   * The widest this column is worth drawing, if there is such a width.
   *
   * Only Description has one (issue #5111). It asks for more room over its minimum than any other
   * column, so on a wide screen the share-out below handed it most of the slack: the column grew
   * until whole sentences fitted inside it, the ellipsis it is styled for stopped appearing, and a
   * table meant to say what the work costs was mostly the paragraph explaining it. A maximum caps
   * what it can take and returns the rest to the columns beside it; past that width the text
   * truncates and the tooltip carries the remainder, which is the behaviour the cell was built for.
   *
   * It is a cap on the *share-out*, not on the table: below the point where everything fits, columns
   * are still at their minimums and the maximum never comes up.
   */
  maximum?: number;
};

export const FEE_SCHEDULE_COLUMN_WIDTHS: Readonly<Record<string, FeeScheduleColumnWidth>> = {
  locked: { preferred: 40, minimum: 40 },
  name: { preferred: 220, minimum: 150 },
  description: { preferred: 280, minimum: 160, maximum: 320 },
  fixedFee: { preferred: 110, minimum: 100 },
  fee: { preferred: 150, minimum: 120 },
  unit: { preferred: 100, minimum: 85 },
  teamMember: { preferred: 190, minimum: 170 },
  billingCode: { preferred: 110, minimum: 95 },
  "mrt-row-actions": { preferred: 100, minimum: 96 },
};

/** What a column asks for when it is not one of the ones above — MRT's own default column width. */
const UNKNOWN_COLUMN_WIDTH: FeeScheduleColumnWidth = { preferred: 180, minimum: 140 };

/**
 * Least useful first, judged by what the reader loses rather than by what the column costs.
 *
 * Code is a reference nobody reads across a row. "Fixed?" is not information at all — it is "no" when
 * the fee has a Per unit and "yes" when it does not, so the column beside it already answers it. Per
 * qualifies the rate. Description is documentation: useful, but the row is identified by its name.
 * Team Member goes last of all, because an unassigned hourly category is the one thing in this table
 * that stops a proposal being hired, and a reader who cannot see the assignment cannot see the
 * problem — the reason `fee-schedule-team-members.spec.ts` reads "Unassigned" out of a draft's row.
 */
const COLUMNS_IN_THE_ORDER_THEY_ARE_GIVEN_UP: readonly string[] = [
  "billingCode",
  "fixedFee",
  "unit",
  "description",
  "teamMember",
];

export function feeScheduleColumnWidth(columnId: string): FeeScheduleColumnWidth {
  return FEE_SCHEDULE_COLUMN_WIDTHS[columnId] ?? UNKNOWN_COLUMN_WIDTH;
}

export type FeeScheduleColumnFit = {
  /** Columns with no room to be drawn, in the shape MRT's `columnVisibility` state wants. */
  hidden: Record<string, boolean>;
  /** What each surviving column should be given, in pixels, summing to the width available. */
  widths: Record<string, number>;
};

/**
 * Fits the fee schedule's columns into the width the table has been given.
 *
 * Two steps. Columns come out while the ones left cannot be drawn even at their minimums — that test
 * uses minimums rather than preferred widths deliberately, since a fixed layout shares out whatever is
 * left over, so the only question a column has to answer to stay is "is there room to draw me at all".
 * Then the leftover space is shared among the survivors in proportion to how much more than their
 * minimum each one asked for, so a description gets the room a "Fixed?" column has no use for.
 *
 * An unmeasured width hides nothing and sizes nothing, leaving the table exactly as it was before it
 * was measured — the first render, and any renderer without `ResizeObserver`.
 */
export function fitFeeScheduleColumns(
  availableWidth: number | undefined,
  presentColumnIds: readonly string[],
): FeeScheduleColumnFit {
  if (availableWidth === undefined) return { hidden: {}, widths: {} };

  const kept = new Set(presentColumnIds);
  const minimumWidthOfKept = () =>
    [...kept].reduce((total, columnId) => total + feeScheduleColumnWidth(columnId).minimum, 0);

  const hidden: Record<string, boolean> = {};
  for (const columnId of COLUMNS_IN_THE_ORDER_THEY_ARE_GIVEN_UP) {
    if (minimumWidthOfKept() <= availableWidth) break;
    if (!kept.delete(columnId)) continue;
    hidden[columnId] = false;
  }

  const survivors = [...kept];
  const totalMinimum = minimumWidthOfKept();
  // Narrower than even the minimums (every optional column is already gone): the columns keep their
  // minimums and the table scrolls, which is the honest answer when the room is genuinely not there.
  const leftover = Math.max(0, availableWidth - totalMinimum);

  const widths = shareOut(survivors, leftover);
  return { hidden, widths };
}

/**
 * Shares `leftover` among `survivors` in proportion to how much more than its minimum each asked for.
 *
 * Capped columns are settled first and then taken out of the share-out, so the room a maximum turns
 * down is offered to the columns that can still use it rather than left as a gap. That has to repeat,
 * because paying one column's cap raises everyone else's share and can push the next column over its
 * own.
 *
 * The loop is bounded by the columns themselves rather than by an argument about when it stops: a
 * pass either settles every remaining column and returns, or removes at least one capped column from
 * `sharing`. So `sharing` strictly shrinks on any pass that does not return, and there can be no more
 * passes than there are columns. Falling out of the loop means every column had a cap and all of them
 * were paid, which leaves nobody to hand the rest of the width to.
 */
function shareOut(survivors: readonly string[], leftover: number): Record<string, number> {
  const widths: Record<string, number> = {};
  let sharing = [...survivors];
  let remaining = leftover;

  while (sharing.length > 0) {
    const totalWanted = sharing.reduce((total, columnId) => {
      const width = feeScheduleColumnWidth(columnId);
      return total + Math.max(0, width.preferred - width.minimum);
    }, 0);

    const shareFor = (columnId: string) => {
      const width = feeScheduleColumnWidth(columnId);
      const wanted = Math.max(0, width.preferred - width.minimum);
      return totalWanted === 0 ? remaining / sharing.length : (wanted / totalWanted) * remaining;
    };

    const capped = sharing.filter((columnId) => {
      const width = feeScheduleColumnWidth(columnId);
      return width.maximum !== undefined && width.minimum + shareFor(columnId) > width.maximum;
    });

    // Nobody is over their cap, so what is left divides cleanly and this is the last pass.
    if (capped.length < 1) {
      sharing.forEach((columnId) => {
        widths[columnId] = Math.floor(feeScheduleColumnWidth(columnId).minimum + shareFor(columnId));
      });
      return widths;
    }

    capped.forEach((columnId) => {
      const width = feeScheduleColumnWidth(columnId);
      widths[columnId] = width.maximum!;
      remaining -= width.maximum! - width.minimum;
    });
    sharing = sharing.filter((columnId) => !capped.includes(columnId));
    remaining = Math.max(0, remaining);
  }

  return widths;
}
